import React, { useCallback, useState } from 'react';
import PageTitle from '../components/PageTitle';
import WhiteboxPage from '../components/WhiteboxPage';
import { FormattedMessage, useIntl } from 'react-intl';
import { PrivacyPolicyLink, UserNoticeLink } from '../components/links';
import Bull from '../components/BulletSeparator';
import { isMobileOidc } from '../utilities/oidc/oidc';
import { useAppSelector } from '../stores/redux.store';
import messages from './StepUpSsoPage.messages';
import { useLocation } from 'react-router-dom';
import Link from '@atlaskit/link';
import {
  useAnalyticsClient,
  usePageViewedEvent,
  useOperationalEvent,
} from '../utilities/analytics/analytics-web-client';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import Heading from '@atlaskit/heading';
import TallButton from '../components/TallButton';
import lockImage from '../images/lock-closed.svg';
import ButtonContainer from '../components/ButtonContainer';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';

export const pageId = 'stepUpSsoScreen';

const SecondaryLinkWrapper = styled.div`
  margin-top: ${gridSize()}px;
`;

const PadlockImageContainer = styled.div`
  background: url(${lockImage}) no-repeat center bottom;
  background-size: contain;
  height: 140px;
  margin: ${gridSize()}px 0 ${gridSize() * 2}px;
`;

export async function stepUpSsoLoader({ request }: { request: Request }) {
  return await fetchPageState('/rest/step-up/sso/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa'],
  });
}

export const StepUpSsoPage = () => {
  const analyticsClient = useAnalyticsClient();
  const oidcContext = useAppSelector(state => state.microbranding.oidcContext);
  const ssoSamlRedirectUrl = useAppSelector(state => state.stepUpSso.ssoRedirectUrl);
  const idpDetails = useAppSelector(state => state.stepUpSso.idp);
  const isMobile = isMobileOidc(oidcContext);
  const { search } = useLocation();
  const navigate = useNavigateSoftOrHard();
  const returnLoginLink = `/login/select-account${search}`;
  const intl = useIntl();
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);

  const eventFlowValue = idpDetails.trusted ? 'trusted-idp' : 'untrusted-idp';
  const shouldOpenWarningModalOnVerify = !idpDetails.trusted && ssoSamlRedirectUrl;

  usePageViewedEvent(pageId, { flow: eventFlowValue });
  useOperationalEvent({
    page: pageId,
    action: `loadStepUpSsoPageSuccessful`,
    subject: 'sli',
  });

  const handleVerify = () => {
    analyticsClient.buttonClickedEvent(pageId, 'verify', {
      flow: eventFlowValue,
    });
    if (ssoSamlRedirectUrl) {
      navigate(ssoSamlRedirectUrl);
    } else {
      captureErrorMessage('StepUpSsoPage verify failed: ssoSamlRedirectUrl not available');
      navigate('/login?errorCode=step_up_sso_saml_redirect_url_not_available');
    }
  };

  const openWarningModal = useCallback(() => {
    analyticsClient.buttonClickedEvent(pageId, 'verify', {
      flow: eventFlowValue,
    });
    setWarningModalOpen(true);
  }, [analyticsClient, eventFlowValue]);

  const closeWarningModal = useCallback(() => {
    analyticsClient.buttonClickedEvent(pageId, 'close-warning-modal');
    setWarningModalOpen(false);
  }, [analyticsClient]);

  const handleReturnToLogin = () => {
    analyticsClient.linkClickedEvent(pageId, 'switchAccount');
  };

  return (
    <>
      <PageTitle title={intl.formatMessage(messages.stepUpSsoHeader)} />
      <WhiteboxPage
        pageId={pageId}
        header={
          <Heading as="h1" size="small">
            <FormattedMessage {...messages.stepUpSsoHeader} />
          </Heading>
        }
        footer={
          <ul className="fine-print">
            <li>
              <PrivacyPolicyLink isMobileLink={isMobile} />
            </li>
            <Bull />
            <li>
              <UserNoticeLink isMobileLink={isMobile} />
            </li>
          </ul>
        }
        whiteboxFooter={
          <>
            {!isMobile && (
              <SecondaryLinkWrapper>
                <Link
                  id="return-to-login"
                  data-testid="return-to-login"
                  href={returnLoginLink}
                  onClick={handleReturnToLogin}>
                  <FormattedMessage {...messages.stepUpSsoReturnToLogin} />
                </Link>
              </SecondaryLinkWrapper>
            )}
          </>
        }>
        <PadlockImageContainer />
        <FormattedMessage {...messages.stepUpSsoDescription1} tagName="p" />
        <FormattedMessage {...messages.stepUpSsoDescription2} tagName="p" />
        <ButtonContainer>
          <TallButton
            id="step-up-sso-button"
            appearance="primary"
            type={'button'}
            onClick={shouldOpenWarningModalOnVerify ? openWarningModal : handleVerify}
            isFullWidth>
            <FormattedMessage {...messages.stepUpSsoVerifyButton} />
          </TallButton>
        </ButtonContainer>
      </WhiteboxPage>
      <ModalTransition>
        {isWarningModalOpen && (
          <Modal onClose={closeWarningModal} width="small">
            <ModalHeader>
              <ModalTitle>
                <FormattedMessage {...messages.stepUpSsoWarningModalHeader} />
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <FormattedMessage
                {...messages.stepUpSsoWarningModalDescription1}
                tagName="p"
                values={{
                  idpRedirectLocation: <strong>{idpDetails.humanReadableRedirectLocation}</strong>,
                }}
              />
              <FormattedMessage {...messages.stepUpSsoWarningModalDescription2} tagName="p" />
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closeWarningModal}>
                <FormattedMessage {...messages.stepUpWarningCancelButton} />
              </Button>
              <Button id="continue-step-up" appearance="primary" onClick={handleVerify}>
                <FormattedMessage {...messages.stepUpSsoWarningLogInButton} />
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export default StepUpSsoPage;
